import { ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useTailoringRoute } from "@/context/TailoringModeContext";
import { useAuth } from "@/hooks/useAuth";
import { useToolAccess } from "@/hooks/useToolAccess";
import { useMembership } from "@/hooks/useMembership";
import { useCredits } from "@/hooks/useCredits";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { 
  Sparkles, 
  Target, 
  BarChart3, 
  Briefcase, 
  FileText, 
  Home, 
  LogOut, 
  User,
  Users,
  Linkedin,
  Search,
  FileSpreadsheet,
  MapPin,
  Coins,
  Lock,
  Building2,
  ChevronDown,
  ClipboardList,
  Zap,
  ShieldCheck,
  DollarSign
} from "lucide-react";
import { useState } from "react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { StudioShell } from "@/components/dashboard/StudioShell";

interface AppLayoutProps {
  children: ReactNode;
  currentPage?: string;
  /** "jobgpt" = the JobGPT-family look (navy/maroon). Opt-in per page while
   *  pages are migrated one at a time; everything else keeps the old theme. */
  variant?: "default" | "jobgpt";
}

export function AppLayout({ children, currentPage = "home", variant = "default" }: AppLayoutProps) {
  const { user, profile, signOut } = useAuth();
  const { isToolLocked } = useToolAccess();
  const { planName } = useMembership();
  const { balance, isLowBalance, isCriticalBalance } = useCredits();
  const navigate = useNavigate();
  const { route: tailoringRoute } = useTailoringRoute();
  const { toast } = useToast();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast({
    title: "Signed Out",
    description: "You have been signed out successfully."
      });
      navigate('/auth', { replace: true });
    } catch (error) {
      toast({
    title: "Signed Out",
    description: "You have been signed out."
      });
      navigate('/auth', { replace: true });
    }
  };

  const getInitials = (name: string) => {
    return name.split(' ').map(word => word.charAt(0)).join('').toUpperCase();
  };

  const getUserDisplayName = () => {
    if (!user) return 'User';
    if (profile?.display_name) return profile.display_name;
    if (profile?.first_name && profile?.last_name) return `${profile.first_name} ${profile.last_name}`;
    if (profile?.first_name) return profile.first_name;
    return user?.email?.split('@')[0] || 'User';
  };

  const jobgpt = variant === "jobgpt";
  const t = {
    shell: "min-h-screen flex w-full bg-gray-50",
    sidebar: "w-64 bg-primary text-white flex flex-col relative z-50",
    logoTile: "w-10 h-10 bg-gradient-to-br from-yellow-400 via-orange-500 to-orange-600 rounded-lg flex items-center justify-center shadow-lg",
    logoSub: "text-sm text-white/60 font-medium",
    label: "text-sm text-white font-bold uppercase tracking-wider mb-3 pb-1 border-b border-white/30",
    active: "bg-primary-dark text-white",
    idle: "text-white/80 hover:bg-primary-hover hover:text-white",
    tile: "w-4 h-4 bg-gradient-to-br from-yellow-400 via-orange-500 to-orange-600 rounded-sm flex items-center justify-center",
    plain: "w-full text-left p-3 rounded-lg transition-colors text-white/80 hover:bg-primary-hover hover:text-white flex items-center gap-3",
  };

  // If user is not authenticated, just render children (for auth pages, etc.)
  if (!user || !user.email || user.is_anonymous) {
    return <>{children}</>;
  }

  if (jobgpt) {
    return (
      <StudioShell currentPage={currentPage} onSignOut={handleSignOut}>
        {children}
      </StudioShell>
    );
  }

  return (
    <TooltipProvider>
      <div className={t.shell}>
        {/* Sidebar */}
        <div className={t.sidebar}>
          <div className="p-6">
            {/* Logo */}
            <button 
              onClick={() => navigate('/')}
              className="flex items-center gap-4 mb-8 w-full text-left hover:bg-white/5 rounded-lg p-2 transition-colors -ml-2"
            >
              <div className={t.logoTile}>
                <Sparkles className="w-5 h-5 text-white" />
              </div>
              <div>
                <div className="text-2xl font-bold">TailorWiz</div>
                <div className={t.logoSub}>TAILORING STUDIO</div>
              </div>
            </button>
            
            {/* Navigation */}
            <nav className="space-y-px">
              <div className={t.label}>Dashboard</div>
              
              <button 
                onClick={() => navigate('/')}
                className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                  currentPage === 'home' ? t.active : t.idle
                }`}
              >
                <Home className="w-4 h-4" />
                Client Dashboard
              </button>
              
              {/* Large spacer */}
              <div className="h-8"></div>
              
              <div className={t.label}>Resume Tools</div>
              
              {/* Resume Tailoring */}
              {isToolLocked('resume-tailoring') ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full text-left p-3 rounded-lg flex items-center gap-3 text-white/40 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center">
                        <Target className="w-3 h-3 text-white/40" />
                      </div>
                      <span className="flex-1">Resume Tailoring</span>
                      <Lock className="w-4 h-4 text-yellow-400/70" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-gray-900 text-white border-gray-700">
                    <p>Upgrade to unlock this tool</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <button 
                  onClick={() => navigate(tailoringRoute)}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                    currentPage === 'resume-tailoring' ? t.active : t.idle
                  }`}
                >
                  <div className={t.tile}>
                    <Target className="w-3 h-3 text-white" />
                  </div>
                  Resume Tailoring
                </button>
              )}
              
              {/* Resume Analysis */}
              {isToolLocked('resume-analysis') ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full text-left p-3 rounded-lg flex items-center gap-3 text-white/40 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center">
                        <BarChart3 className="w-3 h-3 text-white/40" />
                      </div>
                      <span className="flex-1">Resume Analysis</span>
                      <Lock className="w-4 h-4 text-yellow-400/70" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-gray-900 text-white border-gray-700">
                    <p>Upgrade to unlock this tool</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <button 
                  onClick={() => navigate('/?view=resume-analysis')}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                    currentPage === 'resume-analysis' ? t.active : t.idle
                  }`}
                >
                  <div className={t.tile}>
                    <BarChart3 className="w-3 h-3 text-white" />
                  </div>
                  Resume Analysis
                </button>
              )}
               
              {/* Metrics Wizard */}
              {isToolLocked('metrics-wizard') ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full text-left p-3 rounded-lg flex items-center gap-3 text-white/40 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center">
                        <BarChart3 className="w-3 h-3 text-white/40" />
                      </div>
                      <span className="flex-1">Metrics Wizard</span>
                      <Lock className="w-4 h-4 text-yellow-400/70" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-gray-900 text-white border-gray-700">
                    <p>Upgrade to unlock this tool</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <button 
                  onClick={() => navigate('/metrics-wizard')}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                    currentPage === 'metrics-wizard' ? t.active : t.idle
                  }`}
                >
                  <div className={t.tile}>
                    <BarChart3 className="w-3 h-3 text-white" />
                  </div>
                  Metrics Wizard
                </button>
              )}
               
              {/* LinkedIn Wizard */}
              {isToolLocked('linkedin-wizard') ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full text-left p-3 rounded-lg flex items-center gap-3 text-white/40 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center">
                        <Linkedin className="w-3 h-3 text-white/40" />
                      </div>
                      <span className="flex-1">LinkedIn Wizard</span>
                      <Lock className="w-4 h-4 text-yellow-400/70" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-gray-900 text-white border-gray-700">
                    <p>Upgrade to unlock this tool</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <button 
                  onClick={() => navigate('/linkedin-wizard-landing')}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                    currentPage === 'linkedin-wizard' ? t.active : t.idle
                  }`}
                >
                  <div className={t.tile}>
                    <Linkedin className="w-3 h-3 text-white" />
                  </div>
                  LinkedIn Wizard
                </button>
              )}
              
              {/* Job Wizard */}
              {isToolLocked('job-wizard') ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="w-full text-left p-3 rounded-lg flex items-center gap-3 text-white/40 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white/20 rounded-sm flex items-center justify-center">
                        <ClipboardList className="w-3 h-3 text-white/40" />
                      </div>
                      <span className="flex-1">Job Wizard</span>
                      <Lock className="w-4 h-4 text-yellow-400/70" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-gray-900 text-white border-gray-700">
                    <p>Upgrade to unlock this tool</p>
                  </TooltipContent>
                </Tooltip>
              ) : (
                <button 
                  onClick={() => navigate('/qualification-wizard')}
                  className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                    currentPage === 'job-wizard' || currentPage === 'qualification-wizard' ? t.active : t.idle
                  }`}
                >
                  <div className={t.tile}>
                    <ClipboardList className="w-3 h-3 text-white" />
                  </div>
                  Job Wizard
                </button>
              )}
                 
               {/* Spacer */}
              <div className="h-8"></div>
              
              <div className={t.label}>My Libraries</div>
              
              <button 
                onClick={() => navigate('/?view=job-library')}
                className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                  currentPage === 'job-library' ? t.active : t.idle
                }`}
              >
                <div className={t.tile}>
                  <Briefcase className="w-3 h-3 text-white" />
                </div>
                Job Library
              </button>
              
              <button 
                onClick={() => navigate('/?view=resume-library')}
                className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                  currentPage === 'resume-library' ? t.active : t.idle
                }`}
              >
                <div className={t.tile}>
                  <FileText className="w-3 h-3 text-white" />
                </div>
                Resume Library
              </button>
              
              <button 
                onClick={() => navigate('/linkedin-profiles')}
                className={`w-full text-left p-3 rounded-lg transition-colors flex items-center gap-3 ${
                  currentPage === 'linkedin-profiles' ? t.active : t.idle
                }`}
              >
                <div className={t.tile}>
                  <Linkedin className="w-3 h-3 text-white" />
                </div>
                LinkedIn Library
              </button>
              



              {/* Spacer */}
              <div className="h-8"></div>
              
              <div className={t.label}>Personal Info</div>
              
              <button 
                onClick={() => navigate('/profile')}
                className={t.plain}
              >
                <User className="w-4 h-4" />
                Profile
              </button>
              
              <button 
                onClick={handleSignOut}
                className={t.plain}
              >
                <LogOut className="w-4 h-4" />
                Sign Out
              </button>
            </nav>
          </div>
        </div>
        
        {/* Main Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="bg-white border-b border-gray-200 px-8 py-4">
            <div className="flex items-center justify-between">
              <h1 className="text-2xl font-semibold text-gray-900">
                {currentPage === 'home' && 'Client Dashboard'}
                {currentPage === 'word-document-viewer' && 'Tailored Documents Viewer'}
                {currentPage === 'resume-tailoring' && 'Resume Tailoring'}
                {currentPage === 'resume-builder' && 'Resume Editor'}
                {currentPage === 'resume-analysis' && 'Resume Analysis'}
                {currentPage === 'linkedin-wizard' && 'LinkedIn Profile Wizard'}
                {currentPage === 'resume-quantification' && 'Resume Quantification'}
                {currentPage === 'find-hiring-manager' && 'Find Your Hiring Manager'}
                {currentPage === 'profile' && 'Profile Settings'}
                {currentPage === 'jobintel-reports-hub' && (
                  <img src="/images/jobintel-360-header-logo.png" alt="JobIntel 360" className="h-10" />
                )}
                {!['home', 'word-document-viewer', 'resume-tailoring', 'resume-builder', 'resume-analysis', 'linkedin-wizard', 'resume-quantification', 'find-hiring-manager', 'profile', 'jobintel-reports-hub'].includes(currentPage) && 'TailorWiz'}
              </h1>
              
              <div className="flex items-center gap-4">
                <Button variant="outline" size="sm" onClick={() => navigate('/contact')}>
                  Contact Us
                </Button>
                
                <Button variant="outline" size="sm" onClick={handleSignOut}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </Button>
                
                <div 
                  className="flex items-center gap-3 cursor-pointer hover:bg-gray-50 rounded-lg p-2 transition-colors"
                  onClick={() => navigate('/profile')}
                >
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="text-sm">
                      {profile?.display_name ? getInitials(profile.display_name) : <User className="w-5 h-5" />}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{getUserDisplayName()}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-1">
                      {planName} Member • 
                      <button 
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate('/profile');
                        }}
                        className="text-blue-600 hover:text-blue-800 underline"
                      >
                        Profile
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/pricing');
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold border transition-colors ${
                      isCriticalBalance 
                        ? 'border-destructive text-destructive bg-destructive/10 hover:bg-destructive/20' 
                        : isLowBalance 
                          ? 'border-yellow-500 text-yellow-600 bg-yellow-50 hover:bg-yellow-100' 
                          : 'border-primary text-primary bg-primary/10 hover:bg-primary/20'
                    }`}
                  >
                    <Coins className="w-4 h-4" />
                    <span>{balance}</span>
                    <span className="text-xs font-normal">Credits</span>
                  </button>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate('/pricing');
                    }}
                    className="ml-4 bg-gradient-to-r from-primary to-accent hover:from-primary-dark hover:to-accent text-white font-semibold shadow-lg"
                  >
                    <Sparkles className="w-4 h-4 mr-1" />
                    Upgrade
                  </Button>
                </div>
              </div>
            </div>
          </header>

          {/* Page Content */}
          <main className="flex-1 overflow-hidden">
            {children}
          </main>
        </div>
      </div>
    </TooltipProvider>
  );
}
